import { useMemo } from 'react'

import {
  SplitSupplyNoticeDownloadFormat,
  SplitSupplyNoticeNotEditableReason,
  SplitSupplyNoticeSection,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import {
  Alert,
  Flex,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownToBracketRegular } from '@enechain/ecloud-icons'

import { AuditCell } from '~/components/common/MasterDataTable'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { UnsavedChangesDialog } from '~/components/common/UnsavedChangesDialog'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { convertAreaLabel } from '~/models/area'
import { type CivilDate, protoDateToCivilDate } from '~/models/civilDate'
import { useDownloadOwnSplitSupplyNoticeXml } from '~/pages/SplitSupplyPlan/hooks/useDownloadOwnSplitSupplyNoticeXml'
import {
  noticeDetailSearch,
  parseNoticeDetailSearch,
} from '~/pages/SplitSupplyPlan/models/noticeKey'
import { formatCivilDay } from '~/pages/SplitSupplyPlan/models/period'
import { describeSplitSupplyNoticeError } from '~/pages/SplitSupplyPlan/models/serverError'
import { InfoItem } from '~/pages/SplitSupplyPlanDetail/components/InfoItem'
import { NoticeDetailStatus } from '~/pages/SplitSupplyPlanDetail/components/NoticeDetailStatus'
import { NoticeDetailToolbar } from '~/pages/SplitSupplyPlanDetail/components/NoticeDetailToolbar'
import { NoticeValueGrid } from '~/pages/SplitSupplyPlanDetail/components/NoticeValueGrid'
import { useGetOwnSplitSupplyNotice } from '~/pages/SplitSupplyPlanDetail/hooks/useGetOwnSplitSupplyNotice'
import { useNoticeDetailView } from '~/pages/SplitSupplyPlanDetail/hooks/useNoticeDetailView'
import { useNoticeDraftEditing } from '~/pages/SplitSupplyPlanDetail/hooks/useNoticeDraftEditing'
import { useSaveOwnSplitSupplyNoticeValues } from '~/pages/SplitSupplyPlanDetail/hooks/useSaveOwnSplitSupplyNoticeValues'
import {
  changedDayIndexes,
  dayCountOf,
  toSaveRequestPoints,
} from '~/pages/SplitSupplyPlanDetail/models/draft'
import { routePaths } from '~/routes/routePaths'

const NS = 'page:splitSupplyPlanDetail' as const

/**
 * 分割供給計画の詳細 (自社の通告値 1 通。 他社の通告値は `PartnerNoticeDetailPage`)。 通の全地点 × 48 コマを見て、 編集・保存する。
 * 月間の通は「1 日 / 月全体」を切り替えられ、 1 日は日送りで 1 日ずつ、 月全体は対象月の全日を
 * 日付 × 時刻の行で 1 つの表に出して、 1 か月分をまとめて範囲選択・コピー・貼り付け・編集・保存できる。
 * 保存は値を変えた日の分だけ送る (送らなかった日は保存済みの値のまま)。 受渡日 (月間は対象月) を
 * 過ぎた通は表示と XML ダウンロードだけで、 編集の入口は出さない。 広域機関への提出は含まない。
 */
export const OwnNoticeDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useMemo(
    () => parseNoticeDetailSearch(searchParams),
    [searchParams],
  )
  const key = location?.key
  const deliveryDate = location?.deliveryDate

  const isMonthly = key?.section === SplitSupplyNoticeSection.MONTHLY
  const { view, setView, wholeMonth } = useNoticeDetailView(isMonthly)

  const { data, isLoading, isError, error, refetch } =
    useGetOwnSplitSupplyNotice(key, wholeMonth ? 'all' : deliveryDate)
  const { mutate: save, isPending: isSaving } =
    useSaveOwnSplitSupplyNoticeValues()
  const { download } = useDownloadOwnSplitSupplyNoticeXml()

  const fetchedPoints = useMemo(() => data?.points ?? [], [data])
  const {
    points,
    isEditing,
    draft,
    original,
    invalidCells,
    guard,
    startEditing,
    finishEditing,
    setEdited,
  } = useNoticeDraftEditing({
    fetchedPoints,
    wholeMonth,
    deliveryDate,
  })
  // 保存済みの値に空欄のコマがあれば案内する (mock の詳細と同じ。 全コマ埋めて保存すると消える)
  const hasBlankSlots = useMemo(
    () => original.some((point) => point.some((cell) => cell.trim() === '')),
    [original],
  )

  const target =
    location === undefined ? undefined : protoDateToCivilDate(key?.targetDate)

  const goToDay = (day: CivilDate): void => {
    if (key === undefined) return
    setSearchParams(noticeDetailSearch(key, day), { replace: true })
  }

  const handleSave = (): void => {
    if (key === undefined || deliveryDate === undefined || data === undefined) {
      return
    }
    const dayCount = dayCountOf(points)
    const changed = changedDayIndexes(original, draft, dayCount)
    save(
      {
        key,
        points: toSaveRequestPoints(
          points,
          draft,
          changed.length > 0 ? changed : [0],
        ),
      },
      {
        onSuccess: () => {
          finishEditing()
          showSuccessToast({ title: t(`${NS}.save.successTitle`) })
        },
        onError: (err) => {
          showErrorToast({
            title: t(`${NS}.save.errorTitle`),
            description: describeSplitSupplyNoticeError(err, t),
          })
        },
      },
    )
  }

  const breadcrumbItems = [
    { label: t('common:nav.plan') },
    {
      label: t('common:route.planSplitSupply'),
      to: routePaths.planSplitSupply.path,
    },
    { label: t('common:route.planSplitSupplyDetail'), bold: true },
  ]

  return (
    <PageLayout
      breadcrumbItems={breadcrumbItems}
      headerExtra={
        key !== undefined && data !== undefined ? (
          <TertiaryButton
            disabled={isEditing}
            leftIcon={<FaArrowDownToBracketRegular aria-hidden="true" />}
            onClick={() => {
              void download(key, SplitSupplyNoticeDownloadFormat.UNSPECIFIED)
            }}
            size="md"
          >
            {t(`${NS}.downloadXml`)}
          </TertiaryButton>
        ) : undefined
      }
    >
      <PageBody>
        <NoticeDetailStatus
          errorCode={error?.code}
          hasData={data !== undefined}
          hasLocation={location !== undefined}
          isError={isError}
          isLoading={isLoading}
          messages={{
            invalidLink: t(`${NS}.error.invalidLink`),
            notFound: t(`${NS}.error.notFound`),
            fetchFailed: t(`${NS}.error.fetchFailed`),
          }}
          onRetry={() => {
            void refetch()
          }}
        >
          {data !== undefined && (
            <>
              <Flex alignItems="flex-start" flexWrap="wrap" gap={6}>
                <InfoItem label={t(`${NS}.info.infoCode`)}>
                  {t(
                    `${NS}.info.infoName.${isMonthly ? 'monthly' : 'dayAhead'}`,
                  )}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.period`)}>
                  {isMonthly
                    ? t('page:splitSupplyPlan.monthLabel', {
                        year: target?.year,
                        month: target?.month,
                      })
                    : formatCivilDay(deliveryDate)}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.area`)}>
                  {key === undefined ? '—' : convertAreaLabel(key.areaCode)}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.recipient`)}>
                  {data.recipientName === ''
                    ? '—'
                    : `${data.recipientName} (${data.recipientCode})`}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.pointCount`)}>
                  {t(`${NS}.info.pointCountValue`, {
                    count: data.points.length,
                    max: data.maxPointCount.toLocaleString('ja-JP'),
                  })}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.ownBusiness`)}>
                  {data.ownBusiness === undefined
                    ? '—'
                    : `${data.ownBusiness.businessCode} ${data.ownBusiness.businessName}`}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.loadFollowing`)}>
                  {data.loadFollowing === undefined
                    ? '—'
                    : `${data.loadFollowing.businessCode} ${data.loadFollowing.businessName}`}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.loadFollowingContractId`)}>
                  {key?.loadFollowingContractId ?? '—'}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.created`)}>
                  <AuditCell
                    updatedAt={data.createdAt}
                    updatedBy={data.createdBy}
                  />
                </InfoItem>
                <InfoItem label={t(`${NS}.info.updated`)}>
                  <AuditCell
                    updatedAt={data.updatedAt}
                    updatedBy={data.updatedBy}
                  />
                </InfoItem>
              </Flex>

              <NoticeDetailToolbar
                deliveryDate={deliveryDate}
                guardAction={guard.guardAction}
                invalidCount={invalidCells.length}
                isEditing={isEditing}
                isMonthly={isMonthly}
                isSaving={isSaving}
                onDayChange={goToDay}
                onDiscard={finishEditing}
                onSave={handleSave}
                onSetView={setView}
                target={target}
                view={view}
                viewActions={
                  data.editable ? (
                    <TertiaryButton onClick={startEditing} size="md">
                      {t(`${NS}.edit`)}
                    </TertiaryButton>
                  ) : (
                    <Text textStyle="caption" color="Text.midEmphasis">
                      {t(
                        data.notEditableReason ===
                          SplitSupplyNoticeNotEditableReason.DATE_CLOSED &&
                          isMonthly
                          ? `${NS}.notEditable.monthly`
                          : `${NS}.notEditable.dayAhead`,
                      )}
                    </Text>
                  )
                }
                wholeMonth={wholeMonth}
              />

              {hasBlankSlots && (
                <Alert description={t(`${NS}.blankSlots`)} status="error" />
              )}
              <Text textStyle="caption" color="Text.midEmphasis">
                {t(`${NS}.unit`)}
              </Text>
              <NoticeValueGrid
                draft={draft}
                isEditing={isEditing && !isSaving}
                onChange={setEdited}
                points={points}
              />
            </>
          )}
        </NoticeDetailStatus>
      </PageBody>
      <UnsavedChangesDialog
        isOpen={guard.isBlocked}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </PageLayout>
  )
}
